/**
 * 错误边界:子树里有组件渲染时抛错,只换掉这一块 —— 外面的(导航、页面上别的卡)照常。
 *
 * 没有它的时候,任何一个组件渲染时抛错,React 会把整棵树卸掉:整个应用变成一张白页,连导航都没有
 * (2026-10-13 实拍:程序详情页上一张卡拿到形状不对的回包,读一个不存在的字段,整页白掉)。
 *
 * 三个位置:
 * - `<RouteErrorBoundary>`:包在布局的路由出口外面。一页坏了只坏那一页,导航还在,换一页(包括同一页换了查询参数)就恢复。
 * - `<RouteErrorBoundary scope="app">`:入口处包在整个应用外面,兜底 —— 公开页(登录、分享的报价)和布局自己
 *   (侧栏、顶栏、全局的对话抽屉)出错时落到这里。这时候导航也没了,所以那句话不提导航。
 * - `<ErrorBoundary scope="section" label="…">`:包在页面里自己取数的一块外面。一块坏了,页面上别的照常。
 *
 * 发了新版以后,旧的分块文件已经不在服务器上:还开着旧页面的人点到一张没加载过的页,取不到那个文件。这一种「重试」
 * 没用(React 记着那次失败,不会再去取),只有刷新能好 —— 认出来以后只给「刷新页面」。
 *
 * 错误的原文只进控制台(留给排查),不给用户看。事件处理函数和异步回调里抛的错它接不住 —— 那些照旧各自处理。
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Button, Result } from "antd";

type Scope = "page" | "app" | "section";

interface Props {
  children: ReactNode;
  /** page = 布局的路由出口外面(默认);app = 整个应用外面兜底的那一层;section = 页面里的一块 */
  scope?: Scope;
  /** 这一块叫什么(section 的那句话里用;也写进控制台) */
  label?: string;
  /** 变了就把错误收掉重画(换了页、换了程序) */
  resetKey?: string | number;
}

interface State {
  /** null = 没出错;stale = 分块文件取不到(多半是发了新版);broken = 别的 */
  failed: null | "stale" | "broken";
}

/**
 * 取不到分块文件时各家浏览器和 Vite 的说法(Chrome / Firefox / Safari / Vite 的预加载);
 * 最后一种是网关对缺的文件回了一页 HTML(200)时 Safari 说的。
 */
const STALE_CHUNK =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|is not a valid JavaScript MIME type/i;

/** 兜底的那几句话画在自己的浅色底上:有的页面内容区是深色的(超级驾驶舱),字会看不见 */
const PANEL = { background: "#fff", borderRadius: 8 } as const;

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: null };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { failed: STALE_CHUNK.test(message) ? "stale" : "broken" };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(
      "[ErrorBoundary]",
      this.props.label ?? this.props.scope ?? "page",
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prev: Props): void {
    // 只在「钥匙」变了的时候收掉:坏着的时候别的重画不重试(子树还会再抛,就成了死循环)
    if (this.state.failed && prev.resetKey !== this.props.resetKey)
      this.setState({ failed: null });
  }

  private retry = (): void => this.setState({ failed: null });

  render(): ReactNode {
    const { failed } = this.state;
    if (!failed) return this.props.children;
    const scope = this.props.scope ?? "page";
    const stale = failed === "stale";
    const reload = (
      <Button
        key="reload"
        type={stale ? "primary" : "default"}
        size={scope === "section" ? "small" : "middle"}
        onClick={() => window.location.reload()}
      >
        刷新页面
      </Button>
    );
    const retry = (
      <Button
        key="retry"
        type={scope === "section" ? "default" : "primary"}
        size={scope === "section" ? "small" : "middle"}
        onClick={this.retry}
      >
        重试
      </Button>
    );
    if (scope === "section") {
      return (
        <div data-testid="error-boundary-section" style={{ margin: "16px 0" }}>
          <Alert
            type="error"
            showIcon
            message={
              stale
                ? `${this.props.label ?? "这一块"}没有加载出来,可能是系统刚更新过 —— 刷新页面`
                : `${this.props.label ?? "这一块"}出错了,页面上别的不受影响`
            }
            action={stale ? reload : retry}
          />
        </div>
      );
    }
    const title = stale
      ? "页面没有加载出来"
      : scope === "app"
        ? "页面出错了"
        : "这一页出错了";
    const subTitle = stale
      ? "可能是系统刚更新过,或者网络断了一下。刷新页面就好。"
      : scope === "app"
        ? "可以重试;还不行就刷新页面。"
        : "导航和别的页面不受影响。可以重试;还不行就刷新页面。";
    return (
      <div data-testid={`error-boundary-${scope}`} style={PANEL}>
        <Result
          status="error"
          title={title}
          subTitle={subTitle}
          extra={stale ? [reload] : [retry, reload]}
        />
      </div>
    );
  }
}

/** 路由出口外面用:每次导航(包括同一页换了查询参数)都把错误收掉 */
export function RouteErrorBoundary({
  children,
  scope = "page",
}: {
  children: ReactNode;
  scope?: "page" | "app";
}) {
  const location = useLocation();
  return (
    <ErrorBoundary scope={scope} resetKey={location.key}>
      {children}
    </ErrorBoundary>
  );
}
